import React from 'react';
import { Card, Grid } from 'ui-toolkit';
import 'ui-toolkit/scss/modules/_ordered-items.scss';
import filterByType from './utilities/filterByType';
import { IAddRowAction } from './AddRowActions';
import CostDataPurchase from './CostDataPurchase';
import CostTitlePurchase from './CostTitlePurchase/CostTitlePurchase';
import ClosingCostsPaidAtClosing from './Items/ClosingCostsPaidAtClosing';
import EmptyItem from './Items/EmptyItem';
import IHasItemDataProps from './Items/IHasItemDataProps';
import IHasItemEventProps from './Items/IHasItemEventProps';
import IHasLinkProps from './Items/IHasLinkProps';
import IHasMetaDataModalProps from './Items/IHasMetaDataModalProps';
import { IItemWithoutShowSellerProps } from './Items/IItemProps';
import mapItemToItemComponent from './Items/mapItemToItemComponent';
import PurchasePayoff from './Items/PurchasePayoff';
import PurchaseSubtotal from './Items/PurchaseSubtotal';
import { INavigateAwayHelper } from './NavigateAwayHelper';
import { ITotalClosingCosts } from '../../ui-toolkit/cdItems/interfaces/ITotalClosingCosts';
import { IPurchaseTotals } from '../../ui-toolkit/cdItems/interfaces/IPurchaseTotals';
import { ICDV3ViewItem, IPayoffViewItem } from '../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { ICDViewItemSection } from '../../ui-toolkit/cdItems/interfaces/ICDV3ItemSection';
import { eCDV3ItemTypes } from '../../ui-toolkit/enums/eCDV3ItemTypes';
import { convertToFloat } from '../../ui-toolkit/utilities/convertToFloat';

interface IPurchaseCosts extends IHasItemEventProps<ICDV3ViewItem> {
  costs: ITotalClosingCosts;
  totals: IPurchaseTotals;
  dueFromBuyer?: ICDViewItemSection | null;
  paidAlreadyByBuyer?: ICDViewItemSection | null;
  dueToSeller?: ICDViewItemSection | null;
  dueFromSeller?: ICDViewItemSection | null;
  ownerId: string;
  purchasePrice: number;
  sellerAtClosing: number;
  dueFromBuyerActions: { adjustments: IAddRowAction[]; adjustmentsForSeller: IAddRowAction[] };
  paidAlreadyByBuyerActions: { adjustmentsForSeller: IAddRowAction[]; otherCredits: IAddRowAction[] };
  onNavigateAway: INavigateAwayHelper;
  hasEditPermissions?: boolean;
}

export default class PurchaseCosts extends React.Component<IPurchaseCosts> {
  public render() {
    const {
      dueFromBuyer,
      paidAlreadyByBuyer,
      dueToSeller,
      dueFromSeller,
      dueFromBuyerActions,
      onChange,
      onRemove,
      ownerId,
      paidAlreadyByBuyerActions,
      costs,
      totals,
      onNavigateAway,
      hasEditPermissions,
    } = this.props;
    const generateConvertItemToComponentFunction =
      (mapToComponent: (item: ICDV3ViewItem) => any, disableAutoExpand?: boolean) =>
      (item: ICDV3ViewItem | null | undefined, index: number) => {
        if (item) {
          const ItemComponent = mapToComponent(item) as React.ComponentType<
            IItemWithoutShowSellerProps &
              IHasItemDataProps<ICDV3ViewItem> &
              IHasItemEventProps<ICDV3ViewItem> &
              IHasMetaDataModalProps &
              IHasLinkProps
          >;
          return (
            <ItemComponent
              disableAutoExpand={disableAutoExpand}
              key={item.id}
              data={item}
              onChange={onChange}
              onRemove={onRemove}
              onNavigateAway={onNavigateAway}
              hasEditPermissions={hasEditPermissions}
            />
          );
        }
        return <EmptyItem key={index} />;
      };
    const convertItemToComponent = generateConvertItemToComponentFunction(mapItemToItemComponent);
    const convertItemToComponentWithoutAutoExpand = generateConvertItemToComponentFunction(
      mapItemToItemComponent,
      true
    );
    const convertItemToPurchasePayoff = generateConvertItemToComponentFunction(() => (props: any) => (
      <PurchasePayoff ownerId={ownerId} {...props} />
    ));
    const allowToThirdParty = dueFromBuyer
      ? dueFromBuyer.items.filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_to_third_party)).length < 3
      : true;
    const allowOtherToSeller = dueFromBuyer
      ? dueFromBuyer.items.filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_other_to_seller)).length < 5
      : true;
    const allowOtherFromSeller = paidAlreadyByBuyer
      ? paidAlreadyByBuyer.items.filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_other_from_seller)).length < 3
      : true;
    const toThirdPartyActions = allowToThirdParty ? dueFromBuyerActions.adjustments : [];
    const otherToSellerActions = allowOtherToSeller ? dueFromBuyerActions.adjustmentsForSeller : [];
    const forSellerActions = allowOtherFromSeller ? paidAlreadyByBuyerActions.adjustmentsForSeller : [];
    const payoffsWithFiller = [
      ...(dueFromSeller ? (dueFromSeller.items.filter(filterByType(eCDV3ItemTypes.payoff)) as IPayoffViewItem[]) : []),
      null,
      null,
      null,
      null,
    ].slice(0, 4);
    return (
      <Card className="purchase-costs">
        <Grid columnCount={2}>
          <h2 className="purchase-costs__buyer-title">Buyer's transaction at closing</h2>
          <h2 className="purchase-costs__seller-title">Seller's transaction at closing</h2>

          <div className="purchase-costs__section-k">
            <CostTitlePurchase
              name="K. Due from Buyer"
              value={totals.due_from_buyer}
              hasEditPermissions={hasEditPermissions}
            />
            <CostDataPurchase hasEditPermissions={hasEditPermissions}>
              {dueFromBuyer
                ? dueFromBuyer.items
                    .filter(filterByType(eCDV3ItemTypes.sale_price_of_property, eCDV3ItemTypes.personal_property_sale))
                    .map(convertItemToComponent)
                : null}
              <ClosingCostsPaidAtClosing
                ownerId={ownerId}
                total={
                  (costs.subtotals.buyer_at_closing || 0) +
                  (dueFromBuyer
                    ? convertToFloat(
                        (dueFromBuyer.items.find(filterByType(eCDV3ItemTypes.lender_credit)) || ({} as ICDV3ViewItem))
                          .buyer_at_closing
                      ) || 0
                    : 0)
                }
                onNavigateAway={onNavigateAway}
                hasEditPermissions={hasEditPermissions}
              />
            </CostDataPurchase>
          </div>
          <div>
            <div className="purchase-costs__section-m">
              <CostTitlePurchase
                name="M. Due to Seller"
                value={totals.due_to_seller}
                hasEditPermissions={hasEditPermissions}
              />
              <CostDataPurchase hasEditPermissions={hasEditPermissions}>
                {dueToSeller ? (
                  <>
                    {dueToSeller.items
                      .filter(
                        filterByType(eCDV3ItemTypes.sale_price_of_property, eCDV3ItemTypes.personal_property_sale)
                      )
                      .map(convertItemToComponentWithoutAutoExpand)}
                    {dueToSeller.items
                      .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_to_third_party))
                      .map(convertItemToComponent)}
                  </>
                ) : null}
              </CostDataPurchase>
            </div>
          </div>

          <CostDataPurchase
            name="Adjustments"
            className="purchase-costs__section-k-adjustments"
            simpleHeader={true}
            addRowActions={toThirdPartyActions}
            style={{ counterReset: 'cost-data 4' }}
            hasEditPermissions={hasEditPermissions}
          >
            {dueFromBuyer
              ? dueFromBuyer.items
                  .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_to_third_party))
                  .map(convertItemToComponent)
              : null}
          </CostDataPurchase>
          <div />

          <CostDataPurchase
            name="Adjustments for Items Paid by Seller in Advance"
            className="purchase-costs__section-k-adjustments-seller"
            simpleHeader={true}
            addRowActions={otherToSellerActions}
            style={{ counterReset: 'cost-data 7' }}
            hasEditPermissions={hasEditPermissions}
          >
            {dueFromBuyer ? (
              <>
                {dueFromBuyer.items
                  .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_to_seller))
                  .map(convertItemToComponentWithoutAutoExpand)}
                {dueFromBuyer.items
                  .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_other_to_seller))
                  .map(convertItemToComponent)}
              </>
            ) : null}
          </CostDataPurchase>
          <CostDataPurchase
            name="Adjustments for Items Paid by Seller in Advance"
            className="purchase-costs__section-m-adjustments-seller"
            simpleHeader={true}
            addRowActions={otherToSellerActions}
            style={{ counterReset: 'cost-data 8' }}
            hasEditPermissions={hasEditPermissions}
          >
            {dueToSeller
              ? dueToSeller.items
                  .filter(
                    filterByType(
                      eCDV3ItemTypes.buyer_reimbursement_to_seller,
                      eCDV3ItemTypes.buyer_reimbursement_other_to_seller
                    )
                  )
                  .map(convertItemToComponentWithoutAutoExpand)
              : null}
          </CostDataPurchase>

          <div className="purchase-costs__section-l">
            <CostTitlePurchase
              name="L. Paid by/on Behalf of Buyer"
              value={totals.paid_already_by_buyer}
              hasEditPermissions={hasEditPermissions}
            />
            <CostDataPurchase hasEditPermissions={hasEditPermissions}>
              {paidAlreadyByBuyer
                ? [
                    ...paidAlreadyByBuyer.items.filter(
                      filterByType(
                        eCDV3ItemTypes.buyer_deposit,
                        eCDV3ItemTypes.loan_amount,
                        eCDV3ItemTypes.existing_loans_assumed
                      )
                    ),
                    null,
                    ...paidAlreadyByBuyer.items.filter(filterByType(eCDV3ItemTypes.seller_credit)),
                  ].map(convertItemToComponent)
                : null}
            </CostDataPurchase>
            <CostDataPurchase
              name="Other Credits"
              simpleHeader={true}
              addRowActions={paidAlreadyByBuyerActions.otherCredits}
              style={{ counterReset: 'cost-data 5' }}
              hasEditPermissions={hasEditPermissions}
            >
              {paidAlreadyByBuyer
                ? paidAlreadyByBuyer.items
                    .filter(
                      filterByType(
                        eCDV3ItemTypes.buyer_credit_from_seller,
                        eCDV3ItemTypes.buyer_credit_from_third_party
                      )
                    )
                    .map(convertItemToComponent)
                : null}
            </CostDataPurchase>
          </div>
          <div className="purchase-costs__section-n">
            <CostTitlePurchase
              name="N. Due from Seller"
              value={totals.due_from_seller}
              hasEditPermissions={hasEditPermissions}
            />
            <CostDataPurchase hasEditPermissions={hasEditPermissions}>
              {dueFromSeller
                ? dueFromSeller.items
                    .filter(filterByType(eCDV3ItemTypes.seller_excess_deposit))
                    .map(convertItemToComponentWithoutAutoExpand)
                : null}
              <ClosingCostsPaidAtClosing
                ownerId={ownerId}
                total={costs.subtotals.seller_at_closing}
                onNavigateAway={onNavigateAway}
                hasEditPermissions={hasEditPermissions}
              />
              {dueFromSeller
                ? dueFromSeller.items
                    .filter(filterByType(eCDV3ItemTypes.existing_loans_assumed))
                    .map(convertItemToComponentWithoutAutoExpand)
                : null}
              {payoffsWithFiller.map(convertItemToPurchasePayoff)}
              {dueFromSeller
                ? dueFromSeller.items
                    .filter(filterByType(eCDV3ItemTypes.seller_credit))
                    .map(convertItemToComponentWithoutAutoExpand)
                : null}
              {paidAlreadyByBuyer
                ? paidAlreadyByBuyer.items
                    .filter(filterByType(eCDV3ItemTypes.buyer_credit_from_seller))
                    .map(convertItemToComponentWithoutAutoExpand)
                : null}
            </CostDataPurchase>
          </div>

          <CostDataPurchase
            name="Adjustments"
            className="purchase-costs__section-l-adjustments"
            simpleHeader={true}
            style={{ counterReset: 'cost-data 7' }}
            hasEditPermissions={hasEditPermissions}
          >
            <EmptyItem />
            <EmptyItem />
            <EmptyItem />
          </CostDataPurchase>
          <div />

          <CostDataPurchase
            name="Adjustments for Items Unpaid by Seller"
            className="purchase-costs__section-l-adjustments-seller"
            simpleHeader={true}
            addRowActions={forSellerActions}
            style={{ counterReset: 'cost-data 11' }}
            hasEditPermissions={hasEditPermissions}
          >
            {paidAlreadyByBuyer ? (
              <>
                {paidAlreadyByBuyer.items
                  .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_from_seller))
                  .map(convertItemToComponentWithoutAutoExpand)}
                {paidAlreadyByBuyer.items
                  .filter(filterByType(eCDV3ItemTypes.buyer_reimbursement_other_from_seller))
                  .map(convertItemToComponent)}
              </>
            ) : null}
          </CostDataPurchase>
          <CostDataPurchase
            name="Adjustments for Items Unpaid by Seller"
            className="purchase-costs__section-n-adjustments-seller"
            simpleHeader={true}
            addRowActions={forSellerActions}
            style={{ counterReset: 'cost-data 13' }}
            hasEditPermissions={hasEditPermissions}
          >
            {dueFromSeller
              ? dueFromSeller.items
                  .filter(
                    filterByType(
                      eCDV3ItemTypes.buyer_reimbursement_from_seller,
                      eCDV3ItemTypes.buyer_reimbursement_other_from_seller
                    )
                  )
                  .map(convertItemToComponentWithoutAutoExpand)
              : null}
          </CostDataPurchase>

          <div className="purchase-costs__buyer-cash">
            <CostTitlePurchase
              name={`Cash to Close ${totals.buyer_due > 0 ? 'from' : 'to'} Buyer`}
              className="purchase-costs__buyer-cash"
              value={Math.abs(totals.buyer_due)}
            />
            <div className="cost-data cost-data--purchase">
              <div className="cost-data__body">
                <PurchaseSubtotal amount={totals.due_from_buyer} name="Total Due From Buyer (K)" />
                <PurchaseSubtotal amount={totals.paid_already_by_buyer} name="Total Paid by/on Behalf of Buyer (L)" />
              </div>
            </div>
          </div>
          <div className="purchase-costs__seller-cash">
            <CostTitlePurchase
              name={`Cash ${totals.seller_due > 0 ? 'to' : 'from'} Seller`}
              className="purchase-costs__seller-cash"
              value={Math.abs(totals.seller_due)}
            />
            <div className="cost-data cost-data--purchase">
              <div className="cost-data__body">
                <PurchaseSubtotal amount={totals.due_to_seller} name="Total Due To Seller (M)" />
                <PurchaseSubtotal amount={totals.due_from_seller} name="Total Due From Seller (N)" />
              </div>
            </div>
          </div>
        </Grid>
      </Card>
    );
  }
}
